import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { BrandApplication } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { ApplicationStatusBadge } from './application-status-badge';

/** Column model of the applications table. */
export function useApplicationColumns() {
  const { t } = useTranslation('applications');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<BrandApplication>>>(
    () => [
      {
        id: 'brandName',
        accessorKey: 'brandName',
        header: t('columns.brand'),
        cell: ({ row }) => (
          <Link
            to="/applications/$applicationId"
            params={{ applicationId: row.original.id }}
            className="truncate font-medium hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.brandName}
          </Link>
        ),
        meta: { lockVisibility: true },
      },
      {
        id: 'ownerName',
        accessorKey: 'ownerName',
        header: t('columns.owner'),
      },
      {
        id: 'ownerEmail',
        accessorKey: 'ownerEmail',
        header: t('columns.email'),
        cell: ({ row }) => <span className="text-muted-foreground">{row.original.ownerEmail}</span>,
      },
      {
        id: 'countryCode',
        accessorKey: 'countryCode',
        header: t('columns.country'),
        size: 90,
      },
      {
        id: 'productCountBand',
        accessorKey: 'productCountBand',
        header: t('columns.size'),
        cell: ({ row }) => t(`bands.${row.original.productCountBand}`),
        size: 130,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('columns.received'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.createdAt)}>
            {format.relative(row.original.createdAt)}
          </span>
        ),
        size: 130,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <ApplicationStatusBadge status={row.original.status} />,
        size: 140,
      },
    ],
    [format, t],
  );
}
